import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useDisplayName } from '@/composables/useDisplayName';
import { useInvitations } from '@/composables/useInvitations';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useSchoolClasses } from '@/composables/useSchoolClasses';
import { useSchoolClassTeam } from '@/composables/useSchoolClassTeam';
import { useToast } from '@/composables/useToast';
import { IUserWithPermissions } from '@/interfaces';
import { createFakeUserFromInvitation } from '@/utils/user.utils';
import { IFilter } from '@chax-at/prisma-filter-common';
import { useQueryClient } from '@tanstack/vue-query';
import { IAccessRightFilter } from 'common/interface/filter.interface';
import { getSortedAccessRightSubjectIds, hasEqualPermissionsAndSubjectAccess } from 'common/utils/access.right.utils';
import { computed, ComputedRef } from 'vue';
import { useSchoolSubject } from '../schoolSubject/useSchoolSubject';
import { schoolClassAccessKeys } from './schoolClassAccess.keys';

interface ISchoolClassAccessComposable {
  allAccessRights: ComputedRef<Components.Schemas.SchoolClassAccessRightDto[]>;
  // Make sure to send all users with access to correctly determine which access rights
  // to delete, even if only one user is changed
  upsertAccessRights: (
    schoolClassId: number,
    addAccessRightsDto: Components.Schemas.AddAccessRightDto[],
    showToast?: boolean,
  ) => Promise<void>;
  removeAccessRights: (
    accessRights: Components.Schemas.SchoolClassAccessRightDto[],
    showToast?: boolean,
  ) => Promise<void>;
  getHasUserChanges: (classId: number, usersWithAccess: IUserWithPermissions[]) => boolean;
  getResponsibleUserNames: (schoolClass: Components.Schemas.SchoolClassDto) => string;
  getSchoolClassAccessRights: (classId: number) => IUserWithPermissions[];
  getSchoolClassAccessInvitationsAndUsers: (classId: number) => IUserWithPermissions[];
  upsertInvitationAccessRights: (
    schoolClassId: number,
    addInvitationAccessRightsDto: Components.Schemas.AddInvitationAccessRightDto[],
    showToast: boolean,
  ) => Promise<void>;
  upsertAnyAccessRights: (schoolClassId: number, usersWithAccess: IUserWithPermissions[]) => Promise<void>;
  // Send all access rights for a user to update access rights across all classes the user has access to
  upsertClassAccessRightsForUser: (
    userRoleId: number,
    accessRights: Components.Schemas.UpsertUserAccessRightDto[],
    showToast?: boolean,
  ) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useSchoolClassAccess(): ISchoolClassAccessComposable {
  const { catchError, createSuccessToast } = useToast();
  const { allSchoolSubjects } = useSchoolSubject();
  const { refresh: refreshSchoolClasses } = useSchoolClasses();
  const { removeAccessRights: removeAccessRightsExternal, upsertAccessRights: upsertAccessRightsExternal } =
    useSchoolClassTeam();
  const { PENDING_INVITATION_ID } = useInvitations();

  const getAccessRights = async (): Promise<Components.Schemas.SchoolClassAccessRightDto[]> => {
    const filter: IFilter<IAccessRightFilter> = {
      filter: [],
      order: [{ field: 'createdAt', dir: 'desc' }],
    };
    const result = await Api.Routes.SchoolClass.getAccessRights({ $filter: filter });
    return result.rows;
  };

  const accessRightsQueryKey = computed(() => schoolClassAccessKeys.accessRightsList());
  const queryClient = useQueryClient();
  const query = useQueryWithToast({
    queryKey: accessRightsQueryKey,
    queryFn: getAccessRights,
  });

  const getInvitationAccessRights = async (): Promise<Components.Schemas.InvitationAccessRightDto[]> => {
    const filter: IFilter = {
      filter: [],
      order: [{ field: 'createdAt', dir: 'desc' }],
    };
    const result = await Api.Routes.SchoolClass.getInvitationAccessRights({ $filter: filter });
    return result.rows;
  };

  const invitationAccessRightsQueryKey = computed(() => schoolClassAccessKeys.invitationAccessRightsList());
  const invitationQuery = useQueryWithToast({
    queryKey: invitationAccessRightsQueryKey,
    queryFn: getInvitationAccessRights,
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: accessRightsQueryKey }),
      queryClient.invalidateQueries({ queryKey: invitationAccessRightsQueryKey }),
    ]);
  };

  const removeAccessRights = async (
    accessRights: Components.Schemas.SchoolClassAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    await catchError(async () => {
      await removeAccessRightsExternal(accessRights, showToast);
      await refresh();
    });
  };

  const upsertAccessRights = async (
    schoolClassId: number,
    addAccessRightsDto: Components.Schemas.AddAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    await catchError(async () => {
      await upsertAccessRightsExternal(schoolClassId, addAccessRightsDto, showToast);
      await refresh();
    });
  };

  const upsertInvitationAccessRights = async (
    schoolClassId: number,
    addInvitationAccessRightsDto: Components.Schemas.AddInvitationAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    const dto: Components.Schemas.AddInvitationSchoolClassAccessDto = {
      accessRights: addInvitationAccessRightsDto,
    };

    await catchError(async () => {
      await Api.Routes.SchoolClass.upsertInvitationAccessRights({ schoolClassId }, dto);
      await refresh();
      if (showToast) createSuccessToast();
    });
  };

  const allAccessRights = computed<Components.Schemas.SchoolClassAccessRightDto[]>(() => {
    return query.data.value ?? [];
  });

  const { getUserDisplayNameOrEmail } = useDisplayName();

  const getResponsibleUserNames = (schoolClass: Components.Schemas.SchoolClassDto): string => {
    return (
      allAccessRights.value
        .filter((ar) => ar.schoolClass.id === schoolClass.id)
        .map((ar) => getUserDisplayNameOrEmail(ar.user))
        .join(', ') || '-'
    );
  };

  const allInvitationAccessRights = computed<Components.Schemas.InvitationAccessRightDto[]>(() => {
    return invitationQuery.data.value ?? [];
  });

  const getSchoolClassAccessRights = (classId: number): IUserWithPermissions[] => {
    return allAccessRights.value
      .filter((ar) => ar.schoolClass.id === classId)
      .map((ar) => {
        const subjectIds = ar.permissions.subjects?.map((subj) => subj.id) || [];

        return {
          ...ar,
          schoolSubjects: allSchoolSubjects.value.filter((sS) => subjectIds.includes(sS.id)),
        };
      });
  };

  const getSchoolClassAccessInvitationsAndUsers = (classId: number): IUserWithPermissions[] => {
    const users = getSchoolClassAccessRights(classId);

    const invitations: IUserWithPermissions[] = allInvitationAccessRights.value
      .filter((inv) => inv.schoolClassId === classId)
      .map((inv) => {
        const subjectIds = inv.permissions.subjects?.map((s) => s.id) ?? [];
        return {
          isInvitation: true,
          invitationId: inv.invitationId,
          userRoleType: inv.userRoleType,
          permissions: inv.permissions,
          schoolClass: undefined,
          schoolSubjects: allSchoolSubjects.value.filter((s) => subjectIds.includes(s.id)),
          user: createFakeUserFromInvitation(inv.invitationId, inv.email ?? ''),
        };
      });

    return [...users, ...invitations];
  };

  const getHasUserChanges = (classId: number, usersWithAccess: IUserWithPermissions[]): boolean => {
    const currentUserIdsWithAccess = getSchoolClassAccessRights(classId).map((ar) => ar.user.id);
    const newUserIdsWithAccess = usersWithAccess.map((u) => u.user.id);

    const originalAccessRights = getSchoolClassAccessRights(classId);
    for (const oAR of originalAccessRights) {
      const nAR = usersWithAccess.find((u) => u.user.id === oAR.user.id);
      if (!nAR) continue;
      if (!hasEqualPermissionsAndSubjectAccess(oAR, nAR)) {
        return true;
      }
    }

    return (
      currentUserIdsWithAccess.length !== newUserIdsWithAccess.length ||
      currentUserIdsWithAccess.some((id) => !newUserIdsWithAccess.includes(id)) ||
      newUserIdsWithAccess.some((id) => !currentUserIdsWithAccess.includes(id))
    );
  };

  const upsertAnyAccessRights = async (
    schoolClassId: number,
    usersWithAccess: IUserWithPermissions[],
  ): Promise<void> => {
    const normalUsers = usersWithAccess.filter((u) => !u.isInvitation);
    const invitations = usersWithAccess.filter((u) => u.isInvitation);

    // subjectIds come from permissions.subjects: schoolSubjects is resolved against the routed class's
    // subjects, so it is empty for any other class and would widen untouched restrictions to "all"
    await upsertAccessRights(
      schoolClassId,
      normalUsers.map((u) => ({
        email: u.user.email,
        canManageAccessRights: u.permissions.canManageAccessRights,
        canManageStudents: u.permissions.canManageStudents,
        canManagePerformanceAssessments: u.permissions.canManagePerformanceAssessments,
        canManageCompetences: u.permissions.canManageCompetences,
        subjectIds: getSortedAccessRightSubjectIds(u),
      })),
      false,
    );

    await upsertInvitationAccessRights(
      schoolClassId,
      invitations.map((u) => ({
        invitationId: u.isPendingInvitation ? PENDING_INVITATION_ID : u.user.id!,
        canManageAccessRights: u.permissions.canManageAccessRights,
        canManageStudents: u.permissions.canManageStudents,
        canManagePerformanceAssessments: u.permissions.canManagePerformanceAssessments,
        canManageCompetences: u.permissions.canManageCompetences,
        subjectIds: getSortedAccessRightSubjectIds(u),
        email: u.user.email,
      })),
      false,
    );

    await refresh();
    await refreshSchoolClasses();
  };

  const upsertClassAccessRightsForUser = async (
    userRoleId: number,
    accessRights: Components.Schemas.UpsertUserAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    const dto: Components.Schemas.UpsertUserAccessRightsDto = {
      accessRights,
    };

    await catchError(async () => {
      await Api.Routes.User.upsertAccessRights({ userRoleId }, dto);
      await refresh();
      await refreshSchoolClasses();
      if (showToast) createSuccessToast();
    });
  };

  return {
    allAccessRights,
    getHasUserChanges,
    getResponsibleUserNames,
    getSchoolClassAccessInvitationsAndUsers,
    getSchoolClassAccessRights,
    refresh,
    removeAccessRights,
    upsertAccessRights,
    upsertAnyAccessRights,
    upsertInvitationAccessRights,
    upsertClassAccessRightsForUser,
  };
}
